import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import {
  getStreamers, getLiveStreamers, getPeriods, getSettings, getObjectiveSettings, getTeams,
  getMatches, getEvents, getEventOccurrences, getLiveSessions, getLiveDonors, getLiveFollowers, getTopLiveDonors,
  getFollowSuggestions, confirmFollowSuggestion, unconfirmFollowSuggestion,
  Streamer, LiveStreamer, FollowSuggestion, Team, TeamMember, LiveMatch, LiveEvent, EventOccurrence, LiveSession, LiveDonor, LiveFollower, TopLiveDonor,
} from './api';
import StreamerList from './StreamerList';
import LiveAvatar from './LiveAvatar';

interface Props {
  isAdmin: boolean;
  isMobile?: boolean;
  onSelectStreamer?: (s: Streamer) => void;
  refreshKey?: number;
  onStreamerCreated?: () => void;
}

/* ── format helpers ─────────────────────────────────────── */
function fmtNum(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toLocaleString('fr-FR');
}
function fmtHours(mins: number | null | undefined): string {
  if (mins == null) return '—';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return m > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
}
function fmtDuration(mins?: number | null): string {
  if (!mins) return '—';
  return `${Math.floor(mins / 60)}h ${mins % 60}min`;
}
function fmtMoney(v: number | null | undefined): string {
  if (v == null || v <= 0) return '—';
  return `${v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}
function calcPct(n: number | null | undefined, n1: number | null | undefined): number | null {
  if (n == null || n1 == null || n1 === 0) return null;
  return Math.round(((n - n1) / n1) * 100);
}
function fmtPeriod(p: string): string {
  const months = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
  const parts = p.split('-');
  if (parts.length < 2) return p;
  const m = parseInt(parts[1], 10);
  return `${months[m - 1] ?? p} ${parts[0]}`;
}
const MONTHS_FR = ['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Aoû','Sep','Oct','Nov','Déc'];
function fmtPeriodShort(p: string): string {
  const m = p.match(/^(\d{4})-(\d{2})/);
  if (!m) return p;
  return `${MONTHS_FR[parseInt(m[2]) - 1]} ${m[1]}`;
}

/* ── EvoBadge ───────────────────────────────────────────── */
function EvoBadge({ pct }: { pct: number | null }) {
  if (pct === null) return <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>;
  const color = pct > 0 ? '#16a34a' : pct < 0 ? '#dc2626' : '#64748b';
  const arrow = pct > 0 ? '▲' : pct < 0 ? '▼' : '=';
  return <span style={{ color, fontSize: 11, fontWeight: 600 }}>{arrow} {Math.abs(pct)}%</span>;
}

/* ── ObjectiveGauge (Streamer tab) ──────────────────────── */
function ObjectiveGauge({ value, target, label }: { value: number | null; target: number; label: string }) {
  const v = value ?? 0;
  const pct = Math.min(Math.round((v / target) * 100), 100);
  const barColor = pct >= 100 ? '#16a34a' : pct >= 70 ? '#2563eb' : '#f59e0b';
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 4 }}>
        <span>{label}</span>
        <span style={{ fontWeight: 600, color: '#0f172a' }}>{fmtNum(v)} / {fmtNum(target)} <span style={{ color: barColor }}>({pct}%)</span></span>
      </div>
      <div style={{ height: 7, borderRadius: 4, background: '#e2e8f0', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: 4, transition: 'width 0.4s ease' }} />
      </div>
    </div>
  );
}

/* ── StatHolder (generic for Streamer | TeamMember) ─────── */
interface StatHolder {
  diamonds?: number | null;
  live_days?: number | null;
  live_duration_minutes?: number | null;
}

/* ── Mini objective thermometer (Leader list) ───────────── */
function ObjThermo({ s, obj }: { s: StatHolder; obj: { days: number; hours: number; diamonds: number } }) {
  if (s.diamonds == null && s.live_days == null) return <div style={{ width: 8, flexShrink: 0 }} />;
  const metrics = [
    Math.min(1, obj.days > 0 ? (s.live_days ?? 0) / obj.days : 0),
    Math.min(1, obj.hours > 0 ? (s.live_duration_minutes ?? 0) / (obj.hours * 60) : 0),
    Math.min(1, obj.diamonds > 0 ? (s.diamonds ?? 0) / obj.diamonds : 0),
  ];
  const tooltip = [
    `Jours : ${s.live_days ?? 0} / ${obj.days}`,
    `Heures : ${Math.floor((s.live_duration_minutes ?? 0) / 60)} / ${obj.hours}h`,
    `Diamants : ${(s.diamonds ?? 0).toLocaleString('fr-FR')} / ${obj.diamonds.toLocaleString('fr-FR')} 💎`,
  ].join('\n');
  const color = (pct: number) => pct >= 1 ? '#16a34a' : pct >= 0.5 ? '#ea580c' : '#dc2626';
  return (
    <div title={tooltip} style={{ display: 'flex', flexDirection: 'column', gap: 2, width: 8, height: 40, flexShrink: 0, cursor: 'default' }}>
      {[...metrics].reverse().map((pct, i) => (
        <div key={i} style={{
          flex: 1, background: color(pct),
          borderRadius: i === 0 ? '4px 4px 0 0' : i === 2 ? '0 0 4px 4px' : 0,
        }} />
      ))}
    </div>
  );
}

/* ── ObjBars (mobile horizontal) ────────────────────────── */
function ObjBars({ s, obj }: { s: StatHolder; obj: { days: number; hours: number; diamonds: number } }) {
  if (s.diamonds == null && s.live_days == null) return null;
  const metrics = [
    Math.min(1, obj.days > 0 ? (s.live_days ?? 0) / obj.days : 0),
    Math.min(1, obj.hours > 0 ? (s.live_duration_minutes ?? 0) / (obj.hours * 60) : 0),
    Math.min(1, obj.diamonds > 0 ? (s.diamonds ?? 0) / obj.diamonds : 0),
  ];
  const color = (pct: number) => pct >= 1 ? '#16a34a' : pct >= 0.5 ? '#ea580c' : '#dc2626';
  return (
    <div style={{ display: 'flex', gap: 2, marginTop: 6 }}>
      {metrics.map((pct, i) => (
        <div key={i} style={{
          flex: 1, height: 5, background: color(pct),
          borderRadius: i === 0 ? '4px 0 0 4px' : i === 2 ? '0 4px 4px 0' : 0,
        }} />
      ))}
    </div>
  );
}

/* ── TikTok échelon helpers ─────────────────────────────── */
type EchelonStatus = 'montee' | 'maintien' | 'non_maintenu' | null;

function parseEchelon(echelon: string | null | undefined): EchelonStatus {
  if (!echelon || echelon === '-') return null;
  const s = echelon.toLowerCase();
  if (s.includes('non maintenu')) return 'non_maintenu';
  if (s.includes('montée') || s.includes('supérieur') || s.includes('augmentation')) return 'montee';
  if (s.includes('maintien') || s.includes('maintenu')) return 'maintien';
  return null;
}

// Activity thresholds (mirrors backend _ACTIVITE_LEVELS)
const ACTIVITE_THRESHOLDS = [
  { tier: 5, days: 22, hours: 80 },
  { tier: 4, days: 18, hours: 60 },
  { tier: 3, days: 15, hours: 40 },
  { tier: 2, days: 11, hours: 30 },
  { tier: 1, days: 7,  hours: 20 },
] as const;

function getActiviteTier(days: number | null | undefined, mins: number | null | undefined): number {
  const d = days ?? 0, m = mins ?? 0;
  for (const t of ACTIVITE_THRESHOLDS) {
    if (d >= t.days && m >= t.hours * 60) return t.tier;
  }
  return 0;
}
function nextActiviteThreshold(tier: number) {
  return ACTIVITE_THRESHOLDS.slice().reverse().find(t => t.tier === tier + 1) ?? null;
}

/* ── MetricCard ─────────────────────────────────────────── */
function MetricCard({ icon, label, value, pct }: { icon: string; label: string; value: string; pct: number | null }) {
  return (
    <div style={{ flex: '1 1 0', minWidth: 0, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ fontSize: 18 }}>{icon}</div>
      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{value}</div>
      <EvoBadge pct={pct} />
    </div>
  );
}

/* ── TabBtn ─────────────────────────────────────────────── */
function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      padding: '10px 18px', background: 'none', border: 'none',
      borderBottom: `2px solid ${active ? '#2563eb' : 'transparent'}`,
      color: active ? '#2563eb' : '#64748b', fontSize: 13,
      fontWeight: active ? 600 : 400, cursor: 'pointer',
      display: 'flex', alignItems: 'center', transition: 'all 0.15s', marginBottom: -1,
    }}>
      {children}
    </button>
  );
}

/* ── LoadingSpinner ─────────────────────────────────────── */
function LoadingSpinner() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, color: '#94a3b8', fontSize: 14 }}>
      Chargement…
    </div>
  );
}

/* ── TikTok icon ────────────────────────────────────────── */
function TikTokIcon({ color = '#000', size = 14 }: { color?: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.73a4.85 4.85 0 0 1-1.01-.04z"/>
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════
   ActualitesCard — matchs + événements à venir
   ══════════════════════════════════════════════════════════ */

interface ActuItem {
  id: string;
  type: 'match' | 'event';
  eventType?: 'live' | 'concours';
  title: string;
  description: string;
  datetime: string; // ISO sortable
  displayDate: string;
  posterUrl: string | null;
  streamers: Array<{ id: number; name: string; avatar: string | null }>;
}

function ActualitesCard({ isMobile }: { isMobile?: boolean }) {
  const [items, setItems] = useState<ActuItem[]>([]);
  const [liveIds, setLiveIds] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      // Résilient : un appel en échec ne doit pas vider toute la carte.
      const [matchesRes, eventsRes, streamersRes, liveRes] = await Promise.all([
        getMatches().catch(() => ({ data: [] as LiveMatch[] })),
        getEvents().catch(() => ({ data: [] as LiveEvent[] })),
        getStreamers().catch(() => ({ data: [] as Streamer[] })),
        getLiveStreamers().catch(() => ({ data: [] as LiveStreamer[] })),
      ]);

      const nowMs = Date.now();
      const parseLocalDateTime = (value: string) => {
        // Les dates stockées en DB sont souvent "YYYY-MM-DD HH:mm:ss" (heure locale).
        // Comparer cette chaîne à new Date().toISOString() masquait les matchs du jour
        // car " " est inférieur à "T" lexicalement. On compare donc des timestamps.
        const normalized = value.includes('T') ? value : value.replace(' ', 'T');
        const ts = new Date(normalized).getTime();
        return Number.isNaN(ts) ? null : ts;
      };
      const occurrenceDateTime = (date: string, time?: string | null) => {
        const cleanTime = (time || '00:00').slice(0, 5);
        return `${date}T${cleanTime}:00`;
      };
      const liveSet = new Set(liveRes.data.map(s => s.id));
      setLiveIds(liveSet);

      // Map id → streamer complet (avatar + nom)
      const streamerMap = new Map(streamersRes.data.map(s => [s.id, s]));

      const list: ActuItem[] = [];

      // ── Matchs à venir ──
      for (const m of matchesRes.data) {
        if (m.status !== 'scheduled') continue;
        const matchTs = parseLocalDateTime(m.scheduled_at);
        if (matchTs !== null && matchTs < nowMs) continue;
        const normalizedScheduledAt = m.scheduled_at.includes('T') ? m.scheduled_at : m.scheduled_at.replace(' ', 'T');
        const dt = new Date(normalizedScheduledAt);
        const typeLabel: Record<string, string> = {
          officiel: 'Match Officiel', anniversaire: 'Match Anniversaire', autre: m.match_label ?? 'Match'
        };
        // Résoudre les avatars depuis le map si l'avatar direct n'est pas dispo
        const s1 = streamerMap.get(m.streamer1_id);
        list.push({
          id: `match-${m.id}`,
          type: 'match',
          title: m.match_label ?? typeLabel[m.match_type] ?? m.match_type,
          description: `${m.streamer1_name ?? ''} vs ${m.streamer2_name}`,
          datetime: m.scheduled_at,
          displayDate: dt.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
            + ' à ' + dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
          posterUrl: m.poster_url ?? (m.poster_filename ? `/api/match-posters/${m.poster_filename}` : null),
          streamers: [
            { id: m.streamer1_id, name: m.streamer1_name ?? s1?.display_name ?? '', avatar: m.streamer1_avatar ?? s1?.avatar_url ?? null },
            ...(m.streamer2_id
              ? [{ id: m.streamer2_id, name: m.streamer2_name, avatar: m.streamer2_avatar_url ?? streamerMap.get(m.streamer2_id)?.avatar_url ?? null }]
              : []),
          ],
        });
      }

      // ── Occurrences d'événements à venir ──
      for (const ev of eventsRes.data) {
        try {
          const occRes = await getEventOccurrences(ev.id);

          // Résoudre les streamers depuis ev.streamers (si dispo) ou via streamer_ids + streamerMap
          const evStreamers: Array<{ id: number; name: string; avatar: string | null }> =
            (ev.streamers && ev.streamers.length > 0)
              ? ev.streamers.map(s => ({ id: s.streamer_id, name: s.display_name, avatar: s.avatar_url ?? null }))
              : (ev.streamer_ids ?? []).map(id => {
                  const s = streamerMap.get(id);
                  return { id, name: s?.display_name ?? s?.username ?? `#${id}`, avatar: s?.avatar_url ?? null };
                });

          const futureOccs = occRes.data
            .filter(o => {
              if (o.status === 'cancelled') return false;
              const dt = occurrenceDateTime(o.occurrence_date, o.time_start);
              const ts = parseLocalDateTime(dt);
              return ts === null || ts >= nowMs;
            })
            .sort((a, b) => a.occurrence_date.localeCompare(b.occurrence_date));

          // Pour un concours : 1 seule occurrence (la prochaine)
          const occsToShow = ev.event_type === 'concours' ? futureOccs.slice(0, 1) : futureOccs;

          for (const occ of occsToShow) {
            const occDatetime = occurrenceDateTime(occ.occurrence_date, occ.time_start);
            const dt = new Date(occDatetime);
            const imgFile = occ.image_filename || ev.image_filename;
            list.push({
              id: `event-${ev.id}-occ-${occ.id}`,
              type: 'event',
              eventType: ev.event_type,
              title: ev.title,
              description: ev.description ?? '',
              datetime: occDatetime,
              displayDate: dt.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
                + (occ.time_start ? ' à ' + occ.time_start.slice(0, 5) : ''),
              posterUrl: imgFile ? `/api/event-images/${imgFile}` : null,
              streamers: evStreamers,
            });
          }

          // Événement en attente de seuil (démarrage conditionné) : afficher
          // le résumé de récurrence + Y/X même sans occurrence générée.
          if (occsToShow.length === 0 && ev.pending_start && ev.recurrence_summary) {
            list.push({
              id: `event-${ev.id}-pending`,
              type: 'event',
              eventType: ev.event_type,
              title: ev.title,
              description: ev.description ?? '',
              datetime: '9999-12-31T00:00',
              displayDate: `${ev.recurrence_summary} — dès ${ev.selected_count ?? 0}/${ev.min_participants ?? 0} participants`,
              posterUrl: ev.image_filename ? `/api/event-images/${ev.image_filename}` : null,
              streamers: evStreamers,
            });
          }
        } catch {}
      }

      list.sort((a, b) => a.datetime.localeCompare(b.datetime));
      setItems(list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Rafraîchir les statuts live toutes les 10s
  useEffect(() => {
    const iv = setInterval(async () => {
      try {
        const r = await getLiveStreamers();
        setLiveIds(new Set(r.data.map(s => s.id)));
      } catch {}
    }, 10000);
    return () => clearInterval(iv);
  }, []);

  if (loading) return null;
  if (items.length === 0) return null;

  if (isMobile) return <ActualitesMobile items={items} liveIds={liveIds} />;
  return <ActualitesView items={items} liveIds={liveIds} isMobile={false} />;
}

// ── Lightbox plein écran ─────────────────────────────────────
function Lightbox({ url, onClose }: { url: string; onClose: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}
    >
      <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
      <img
        src={url}
        alt=""
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '92vw', maxHeight: '92vh', objectFit: 'contain', borderRadius: 10, cursor: 'default', boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }}
      />
    </div>
  );
}

// ── Carte item (partagée desktop + mobile) ───────────────────
function ActuItemCard({ item, liveIds, width, posterH, onLightbox }: {
  item: ActuItem; liveIds: Set<number>; width: number; posterH: number;
  onLightbox: (url: string) => void;
}) {
  const badgeBg = item.type === 'match' ? '#2563eb' : (item.eventType === 'concours' ? '#7c3aed' : '#0891b2');
  const badgeLabel = item.type === 'match' ? '🥊 Match' : (item.eventType === 'concours' ? '🏆 Concours' : '📡 Live');

  return (
    <div style={{ flexShrink: 0, width, borderRadius: 12, overflow: 'hidden', background: '#fff', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column' }}>
      {/* Affiche — cliquable */}
      <div
        style={{ position: 'relative', width: '100%', height: posterH, background: '#f1f5f9', flexShrink: 0, cursor: item.posterUrl ? 'zoom-in' : 'default' }}
        onClick={() => item.posterUrl && onLightbox(item.posterUrl)}
      >
        {item.posterUrl ? (
          <img src={item.posterUrl} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 12 }}>
            <span style={{ fontSize: 36 }}>{item.type === 'match' ? '🥊' : '📅'}</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textAlign: 'center' }}>{item.title}</span>
          </div>
        )}
        <div style={{ position: 'absolute', top: 8, left: 8 }}>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: badgeBg, color: '#fff', opacity: 0.92 }}>{badgeLabel}</span>
        </div>
        {item.posterUrl && (
          <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.45)', borderRadius: 6, padding: '3px 6px', fontSize: 11, color: '#fff' }}>🔍</div>
        )}
      </div>

      {/* Info sous l'affiche */}
      <div style={{ padding: '10px 11px 12px', display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {item.streamers.map(s => {
            const isLive = liveIds.has(s.id);
            return (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', overflow: 'hidden', background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#7c3aed' }}>
                    {s.avatar ? <img src={s.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : s.name.charAt(0).toUpperCase()}
                  </div>
                  {isLive && <div style={{ position: 'absolute', bottom: 0, right: -1, width: 9, height: 9, borderRadius: '50%', background: '#dc2626', border: '2px solid #fff' }} />}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</div>
                  {isLive && <div style={{ fontSize: 10, color: '#dc2626', fontWeight: 600, lineHeight: 1 }}>● En live</div>}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', lineHeight: 1.3, marginTop: 2 }}>{item.title}</div>
        {item.description && (
          <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' } as React.CSSProperties}>
            {item.description}
          </div>
        )}
        <div style={{ marginTop: 'auto', paddingTop: 4, borderTop: '1px solid #f1f5f9', fontSize: 11, fontWeight: 600, color: '#7c3aed' }}>
          📅 {item.displayDate}
        </div>
      </div>
    </div>
  );
}

// ── Vue desktop : scroll horizontal ─────────────────────────
function ActualitesView({ items, liveIds, isMobile }: { items: ActuItem[]; liveIds: Set<number>; isMobile: boolean }) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);

  const CARD_W = 190;
  const POSTER_H = 255;

  return (
    <>
      {lightbox && <Lightbox url={lightbox} onClose={() => setLightbox(null)} />}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '18px 20px', flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>📣</span>
            <span style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>Actualités</span>
            <span style={{ fontSize: 12, color: '#94a3b8', background: '#f1f5f9', borderRadius: 10, padding: '1px 8px' }}>{items.length}</span>
          </div>
          {items.length > 3 && (
            <div style={{ display: 'flex', gap: 4 }}>
              {(['left', 'right'] as const).map(dir => (
                <button key={dir}
                  onClick={() => scrollRef.current?.scrollBy({ left: dir === 'left' ? -(CARD_W + 12) * 2 : (CARD_W + 12) * 2, behavior: 'smooth' })}
                  style={{ width: 26, height: 26, borderRadius: '50%', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                  {dir === 'left' ? '‹' : '›'}
                </button>
              ))}
            </div>
          )}
        </div>
        <div ref={scrollRef} style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 4, scrollbarWidth: 'thin', scrollbarColor: '#e2e8f0 transparent' }}>
          {items.map(item => (
            <ActuItemCard key={item.id} item={item} liveIds={liveIds} width={CARD_W} posterH={POSTER_H} onLightbox={setLightbox} />
          ))}
        </div>
      </div>
    </>
  );
}

// ── Vue mobile : carrousel plein écran ──────────────────────
export function ActualitesMobile({ items, liveIds }: { items: ActuItem[]; liveIds: Set<number> }) {
  const [idx, setIdx] = useState(0);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const next = useCallback(() => setIdx(i => (i + 1) % items.length), [items.length]);
  const prev = useCallback(() => setIdx(i => (i - 1 + items.length) % items.length), [items.length]);

  // Auto-défilement 3s
  useEffect(() => {
    if (items.length <= 1) return;
    timerRef.current = setInterval(next, 3000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [next, items.length]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (items.length > 1) timerRef.current = setInterval(next, 3000);
  }, [next, items.length]);

  if (items.length === 0) return <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 32 }}>Aucune actualité</div>;

  const item = items[idx];
  const badgeBg = item.type === 'match' ? '#2563eb' : (item.eventType === 'concours' ? '#7c3aed' : '#0891b2');
  const badgeLabel = item.type === 'match' ? '🥊 Match' : (item.eventType === 'concours' ? '🏆 Concours' : '📡 Live');

  return (
    <>
      {lightbox && <Lightbox url={lightbox} onClose={() => setLightbox(null)} />}
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {/* Affiche — grandit pour remplir l'espace disponible */}
        <div
          style={{ position: 'relative', flex: 1, minHeight: 150, background: '#0f172a', cursor: item.posterUrl ? 'zoom-in' : 'default', overflow: 'hidden' }}
          onClick={() => item.posterUrl && setLightbox(item.posterUrl)}
        >
          {item.posterUrl
            ? <img src={item.posterUrl} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
            : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48 }}>{item.type === 'match' ? '🥊' : '📅'}</div>
          }
          <div style={{ position: 'absolute', top: 12, left: 12 }}>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20, background: badgeBg, color: '#fff' }}>{badgeLabel}</span>
          </div>
          {items.length > 1 && (
            <>
              <button onClick={e => { e.stopPropagation(); prev(); resetTimer(); }}
                style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,0,0,0.4)', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
              <button onClick={e => { e.stopPropagation(); next(); resetTimer(); }}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,0,0,0.4)', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
            </>
          )}
          {item.posterUrl && <div style={{ position: 'absolute', bottom: 10, right: 10, background: 'rgba(0,0,0,0.4)', borderRadius: 6, padding: '3px 7px', fontSize: 12, color: '#fff' }}>🔍</div>}
        </div>

        {/* Dots */}
        {items.length > 1 && (
          <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'center', gap: 6, padding: '8px 0 4px', background: '#0f172a' }}>
            {items.map((_, i) => (
              <div key={i} onClick={() => { setIdx(i); resetTimer(); }}
                style={{ width: i === idx ? 18 : 6, height: 6, borderRadius: 3, background: i === idx ? '#7c3aed' : 'rgba(255,255,255,0.3)', transition: 'all 0.25s', cursor: 'pointer' }} />
            ))}
          </div>
        )}

        {/* Info — taille fixe, toujours visible sans scroll */}
        <div style={{ flexShrink: 0, padding: '10px 14px 12px', display: 'flex', flexDirection: 'column', gap: 5, background: '#fff', borderTop: '1px solid #f1f5f9' }}>
          {/* Streamers */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {item.streamers.map(s => {
              const isLive = liveIds.has(s.id);
              return (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div style={{ position: 'relative' }}>
                    <div style={{ width: 26, height: 26, borderRadius: '50%', overflow: 'hidden', background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#7c3aed' }}>
                      {s.avatar ? <img src={s.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : s.name.charAt(0).toUpperCase()}
                    </div>
                    {isLive && <div style={{ position: 'absolute', bottom: 0, right: -1, width: 8, height: 8, borderRadius: '50%', background: '#dc2626', border: '2px solid #fff' }} />}
                  </div>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>{s.name}</div>
                    {isLive && <div style={{ fontSize: 9, color: '#dc2626', fontWeight: 600, lineHeight: 1 }}>● En live</div>}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>{item.title}</div>
          {item.description && (
            <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' } as React.CSSProperties}>
              {item.description}
            </div>
          )}
          <div style={{ fontSize: 11, fontWeight: 600, color: '#7c3aed' }}>📅 {item.displayDate}</div>
        </div>
      </div>
    </>
  );
}

/* ══════════════════════════════════════════════════════════
   LiveStreamersCard — card "En live" (droite du Streamer tab)
   ══════════════════════════════════════════════════════════ */
// Cache module-level partagé : évite que la card "clignote" / se vide quand
// le composant est démonté/remonté (changement d'onglet, etc.).
let _liveListCache: LiveStreamer[] = [];

function LiveStreamersCard({ isMobile }: { isMobile?: boolean }) {
  const [liveList, setLiveList] = useState<LiveStreamer[]>(() => _liveListCache);

  const loadLive = useCallback(async () => {
    try {
      const r = await getLiveStreamers();
      _liveListCache = r.data;
      setLiveList(r.data);
    } catch {}
  }, []);

  useEffect(() => {
    loadLive();
    const iv = setInterval(loadLive, 5000);
    return () => clearInterval(iv);
  }, [loadLive]);

  return (
    <div style={{
      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
      padding: isMobile ? '12px 12px' : '18px 20px',
      width: '100%',
      minWidth: 0,
      maxWidth: isMobile ? '100%' : 320,
      boxSizing: 'border-box',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: isMobile ? 10 : 14, minWidth: 0 }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#dc2626', display: 'inline-block', animation: 'livePulse 1.5s ease-in-out infinite' }} />
        <span style={{ fontWeight: 700, fontSize: isMobile ? 12 : 13, color: '#0f172a', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Actuellement en live</span>
        <style>{`@keyframes livePulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
      </div>

      {liveList.length === 0 ? (
        <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>
          Aucun streamer en live
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 6 : 10, minWidth: 0 }}>
          {liveList.map(s => {
            const initials = (s.display_name || s.username).slice(0, 2).toUpperCase();
            const liveUrl = `https://www.tiktok.com/@${s.username}/live`;
            return (
              <a key={s.id} href={liveUrl} target="_blank" rel="noreferrer"
                style={{ display: 'grid', gridTemplateColumns: `${isMobile ? 32 : 36}px minmax(0, 1fr) auto`, alignItems: 'center', gap: isMobile ? 7 : 10, textDecoration: 'none', borderRadius: 8, padding: isMobile ? '6px 6px' : '6px 8px', transition: 'background 0.12s', cursor: 'pointer', minWidth: 0 }}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = '#fef2f2'}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}>
                <LiveAvatar url={s.avatar_url} initials={initials} size={isMobile ? 32 : 36} isLive={true} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: isMobile ? 12 : 13, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.display_name || s.username}</div>
                  <div style={{ fontSize: isMobile ? 10 : 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>@{s.username}</div>
                </div>
                <div title="Spectateurs en direct" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, minWidth: isMobile ? 42 : 48, maxWidth: isMobile ? 64 : undefined, fontSize: isMobile ? 11 : 12, fontWeight: 700, color: '#dc2626', background: '#fef2f2', borderRadius: 999, padding: isMobile ? '3px 6px' : '3px 8px', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                  👁 {(s.viewers ?? 0).toLocaleString('fr-FR')}
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FollowAgencyCard({ isMobile }: { isMobile?: boolean }) {
  const [suggestions, setSuggestions] = useState<FollowSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const load = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const r = await getFollowSuggestions();
      setSuggestions(r.data);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
    const iv = setInterval(() => load(false), 30000);
    return () => clearInterval(iv);
  }, [load]);

  const toggleFollow = async (suggestion: FollowSuggestion) => {
    setUpdatingId(suggestion.id);
    try {
      if (suggestion.is_followed) {
        await unconfirmFollowSuggestion(suggestion.id);
      } else {
        await confirmFollowSuggestion(suggestion.id);
      }
      setSuggestions(prev => prev.map(s => s.id === suggestion.id
        ? { ...s, is_followed: !suggestion.is_followed }
        : s));
    } finally {
      setUpdatingId(null);
    }
  };

  const followedCount = suggestions.filter(s => s.is_followed).length;

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: isMobile ? '14px 12px' : '16px 18px', marginTop: isMobile ? 0 : 16, width: '100%', maxWidth: isMobile ? '100%' : 320, minWidth: 0, boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>Abonne-toi</div>
          <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>Soutiens les streamers de l'agence</div>
        </div>
        <span style={{ borderRadius: 999, padding: '4px 9px', background: '#f0fdf4', color: '#15803d', fontSize: 11, fontWeight: 800 }}>{followedCount}/{suggestions.length}</span>
      </div>

      {loading ? (
        <div style={{ color: '#94a3b8', fontSize: 12, textAlign: 'center', padding: '16px 0' }}>Chargement...</div>
      ) : suggestions.length === 0 ? (
        <div style={{ color: '#64748b', fontSize: 12, fontWeight: 700, textAlign: 'center', padding: '16px 0', background: '#f8fafc', borderRadius: 9 }}>
          Aucun streamer disponible.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, maxHeight: isMobile ? 'none' : 320, overflowY: isMobile ? 'visible' : 'auto', paddingRight: isMobile ? 0 : 3 }}>
          {suggestions.map(s => {
            const initials = (s.display_name || s.username).slice(0, 2).toUpperCase();
            const url = `https://www.tiktok.com/@${s.username}${s.is_live ? '/live' : ''}`;
            return (
              <div key={s.id} style={{ display: 'grid', gridTemplateColumns: `${isMobile ? 36 : 40}px minmax(0, 1fr) 34px`, alignItems: 'center', gap: 9, border: '1px solid #f1f5f9', borderRadius: 10, padding: '8px 9px', background: s.is_live ? '#fff7f7' : '#fff' }}>
                <a href={url} target="_blank" rel="noreferrer" title={s.is_live ? 'Rejoindre le live' : 'Ouvrir le profil'} style={{ display: 'flex', textDecoration: 'none' }}>
                  <LiveAvatar url={s.avatar_url} initials={initials} size={isMobile ? 36 : 40} isLive={s.is_live} />
                </a>
                <a href={url} target="_blank" rel="noreferrer" title={s.is_live ? 'Rejoindre le live' : 'Ouvrir le profil'} style={{ minWidth: 0, color: '#0f172a', textDecoration: 'none' }}>
                  <div style={{ fontSize: 13, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.display_name || s.username}</div>
                  <div style={{ fontSize: 10, color: s.is_live ? '#dc2626' : '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    @{s.username}{s.is_live ? ` · en live · ${s.viewers ?? 0} viewer${(s.viewers ?? 0) > 1 ? 's' : ''}` : ''}
                  </div>
                </a>
                <button onClick={() => toggleFollow(s)} disabled={updatingId === s.id}
                  aria-label={s.is_followed ? `Marquer @${s.username} comme non abonné` : `Marquer @${s.username} comme abonné`}
                  aria-pressed={s.is_followed}
                  title={s.is_followed ? 'Abonné : cliquer pour corriger' : 'Non abonné : cliquer après abonnement'}
                  style={{ border: 'none', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', background: s.is_followed ? '#16a34a' : '#dc2626', color: '#fff', fontSize: 17, fontWeight: 900, cursor: updatingId === s.id ? 'wait' : 'pointer', opacity: updatingId === s.id ? 0.55 : 1, transition: 'background 0.15s, transform 0.15s' }}>
                  ✓
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   MobileTabLayout — menu bas 3 onglets (mobile dashboard)
   ══════════════════════════════════════════════════════════ */
export function TopDonorsCard({ streamerId, isMobile }: { streamerId: number; isMobile?: boolean }) {
  const [donors, setDonors] = useState<TopLiveDonor[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getTopLiveDonors(streamerId, undefined, 10);
      setDonors(r.data);
    } catch {
      setDonors([]);
    } finally {
      setLoading(false);
    }
  }, [streamerId]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [load]);

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: isMobile ? '12px 12px' : '16px 18px', width: '100%', minWidth: 0, maxWidth: isMobile ? '100%' : 320, boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: isMobile ? 13 : 14, color: '#0f172a' }}>Top donateurs</div>
          <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>Mois en cours</div>
        </div>
        <span style={{ borderRadius: 999, padding: '4px 8px', background: '#f5f3ff', color: '#7c3aed', fontSize: 11, fontWeight: 800 }}>{donors.length}</span>
      </div>
      {loading ? (
        <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '14px 0' }}>Chargement...</div>
      ) : donors.length === 0 ? (
        <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '14px 0' }}>Aucun donateur ce mois-ci.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 7 : 8 }}>
          {donors.map((d, index) => {
            const name = d.donor_nickname || d.donor_username;
            const initials = name.slice(0, 2).toUpperCase();
            const url = d.profile_url || `https://www.tiktok.com/@${d.donor_username}`;
            return (
              <a key={d.donor_username} href={url} target="_blank" rel="noreferrer"
                style={{ display: 'grid', gridTemplateColumns: `${isMobile ? 32 : 36}px minmax(0, 1fr) auto`, alignItems: 'center', gap: isMobile ? 8 : 10, textDecoration: 'none', color: '#0f172a', borderRadius: 9, padding: isMobile ? '6px 6px' : '7px 8px', background: index === 0 ? '#f8fafc' : 'transparent', border: index === 0 ? '1px solid #f1f5f9' : '1px solid transparent' }}>
                <LiveAvatar url={d.donor_avatar_url} initials={initials} size={isMobile ? 32 : 36} isLive={!!d.is_live} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                  <div style={{ fontSize: isMobile ? 10 : 11, color: d.is_live ? '#dc2626' : '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    @{d.donor_username}{d.is_live ? ' - en live' : ''}
                  </div>
                </div>
                <div style={{ fontSize: isMobile ? 11 : 12, fontWeight: 900, color: '#b45309', background: '#fffbeb', borderRadius: 999, padding: isMobile ? '3px 7px' : '4px 8px', whiteSpace: 'nowrap' }}>
                  🪙 {fmtNum(d.coins)}
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

function fmtLiveDate(value: string): string {
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

function fmtLiveTime(value?: string | null): string {
  if (!value) return 'En cours';
  const d = new Date(value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value.slice(11, 16) || value;
  return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

export function MyLivesCard({ streamerId, isMobile }: { streamerId: number; isMobile?: boolean }) {
  const [tab, setTab] = useState<'lives' | 'top'>('lives');
  const [lives, setLives] = useState<LiveSession[]>([]);
  const [topDonors, setTopDonors] = useState<TopLiveDonor[]>([]);
  const [loading, setLoading] = useState(true);
  const [topLoading, setTopLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedView, setSelectedView] = useState<'donors' | 'followers'>('donors');
  const [donors, setDonors] = useState<Record<number, LiveDonor[]>>({});
  const [followers, setFollowers] = useState<Record<number, LiveFollower[]>>({});
  const [donorsLoading, setDonorsLoading] = useState(false);
  const [followersLoading, setFollowersLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const r = await getLiveSessions(streamerId);
        if (!cancelled) setLives(r.data);
      } catch {
        if (!cancelled) setLives([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [streamerId]);

  const loadTopDonors = useCallback(async () => {
    setTopLoading(true);
    try {
      const r = await getTopLiveDonors(streamerId, undefined, 10);
      setTopDonors(r.data);
    } catch {
      setTopDonors([]);
    } finally {
      setTopLoading(false);
    }
  }, [streamerId]);

  useEffect(() => {
    loadTopDonors();
    const iv = setInterval(loadTopDonors, 15000);
    return () => clearInterval(iv);
  }, [loadTopDonors]);

  const loadDonors = async (liveId: number) => {
    if (donors[liveId]) return;
    setDonorsLoading(true);
    try {
      const r = await getLiveDonors(liveId);
      setDonors(prev => ({ ...prev, [liveId]: r.data }));
    } catch {
      setDonors(prev => ({ ...prev, [liveId]: [] }));
    } finally {
      setDonorsLoading(false);
    }
  };

  const loadFollowers = async (liveId: number) => {
    if (followers[liveId]) return;
    setFollowersLoading(true);
    try {
      const r = await getLiveFollowers(liveId);
      setFollowers(prev => ({ ...prev, [liveId]: r.data }));
    } catch {
      setFollowers(prev => ({ ...prev, [liveId]: [] }));
    } finally {
      setFollowersLoading(false);
    }
  };

  /** Ouvre/ferme un live. Si view est précisé, force la vue (donors/followers). */
  const openLive = async (liveId: number, view?: 'donors' | 'followers') => {
    const targetView = view ?? selectedView;
    const sameLive = selectedId === liveId;
    if (sameLive && (!view || view === selectedView)) {
      // re-clic identique → ferme
      setSelectedId(null);
      return;
    }
    setSelectedId(liveId);
    setSelectedView(targetView);
    if (targetView === 'donors') await loadDonors(liveId);
    else await loadFollowers(liveId);
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: isMobile ? '14px 14px' : '16px 20px', marginTop: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>Mes lives</div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Mois en cours</div>
        </div>
        <span style={{ fontSize: 12, color: '#64748b', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 999, padding: '3px 9px' }}>{tab === 'lives' ? lives.length : topDonors.length}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 4, marginBottom: 12 }}>
        {[
          { id: 'lives' as const, label: 'Mes lives' },
          { id: 'top' as const, label: 'Top donateurs' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ border: 'none', borderRadius: 7, padding: isMobile ? '8px 6px' : '7px 10px', background: tab === t.id ? '#fff' : 'transparent', color: tab === t.id ? '#0f172a' : '#64748b', fontWeight: 800, fontSize: isMobile ? 12 : 13, cursor: 'pointer', boxShadow: tab === t.id ? '0 1px 3px rgba(15,23,42,0.08)' : undefined }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'top' ? (
        topLoading ? (
          <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '14px 0' }}>Chargement...</div>
        ) : topDonors.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '14px 0' }}>Aucun donateur ce mois-ci.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 7 : 8 }}>
            {topDonors.map((d, index) => {
              const name = d.donor_nickname || d.donor_username;
              const initials = name.slice(0, 2).toUpperCase();
              const url = d.profile_url || `https://www.tiktok.com/@${d.donor_username}`;
              return (
                <a key={d.donor_username} href={url} target="_blank" rel="noreferrer"
                  style={{ display: 'grid', gridTemplateColumns: `${isMobile ? 32 : 36}px minmax(0, 1fr) auto`, alignItems: 'center', gap: isMobile ? 8 : 10, textDecoration: 'none', color: '#0f172a', borderRadius: 9, padding: isMobile ? '6px 6px' : '7px 8px', background: index === 0 ? '#f8fafc' : 'transparent', border: index === 0 ? '1px solid #f1f5f9' : '1px solid transparent' }}>
                  <LiveAvatar url={d.donor_avatar_url} initials={initials} size={isMobile ? 32 : 36} isLive={!!d.is_live} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                    <div style={{ fontSize: isMobile ? 10 : 11, color: d.is_live ? '#dc2626' : '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      @{d.donor_username}{d.is_live ? ' - en live' : ''}
                    </div>
                  </div>
                  <div style={{ fontSize: isMobile ? 11 : 12, fontWeight: 900, color: '#b45309', background: '#fffbeb', borderRadius: 999, padding: isMobile ? '3px 7px' : '4px 8px', whiteSpace: 'nowrap' }}>
                    🪙 {fmtNum(d.coins)}
                  </div>
                </a>
              );
            })}
          </div>
        )
      ) : loading ? (
        <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '14px 0' }}>Chargement...</div>
      ) : lives.length === 0 ? (
        <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '14px 0' }}>Aucun live historise ce mois-ci.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {lives.map(live => {
            const isOpen = selectedId === live.id;
            const liveDonors = donors[live.id] ?? [];
            const liveFollowers = followers[live.id] ?? [];
            return (
              <div key={live.id} style={{ border: `1px solid ${isOpen ? '#bfdbfe' : '#e2e8f0'}`, borderRadius: 10, overflow: 'hidden', background: isOpen ? '#f8fbff' : '#fff' }}>
                <div style={{ padding: isMobile ? '8px 8px' : '10px 12px', display: 'grid', gridTemplateColumns: isMobile ? '72px repeat(3, minmax(0, 1fr))' : '120px 1fr', gap: isMobile ? 5 : 12, alignItems: 'stretch' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 700, color: '#0f172a' }}>{fmtLiveDate(live.started_at)}</div>
                    <div style={{ fontSize: isMobile ? 9 : 11, color: '#64748b', marginTop: 2, lineHeight: 1.2 }}>{fmtLiveTime(live.started_at)} - {fmtLiveTime(live.ended_at)}</div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3, minmax(0, 1fr))' : 'repeat(3, 1fr)', gap: isMobile ? 5 : 6, gridColumn: isMobile ? 'span 3' : undefined }}>
                    <MiniLiveMetric label="👁 Viewers" value={fmtNum(live.max_viewers)} />
                    <MiniLiveMetric label="👥 Followers" value={fmtNum(live.new_followers)}
                      onClick={() => openLive(live.id, 'followers')}
                      active={isOpen && selectedView === 'followers'} />
                    <MiniLiveMetric label="🪙 Pièces" value={fmtNum(live.total_coins)}
                      onClick={() => openLive(live.id, 'donors')}
                      active={isOpen && selectedView === 'donors'} />
                  </div>
                </div>
                {isOpen && selectedView === 'donors' && (
                  <div style={{ borderTop: '1px solid #e2e8f0', padding: '10px 12px', background: '#fff' }}>
                    {donorsLoading && !donors[live.id] ? (
                      <div style={{ color: '#94a3b8', fontSize: 12 }}>Chargement des donateurs...</div>
                    ) : liveDonors.length === 0 ? (
                      <div style={{ color: '#94a3b8', fontSize: 12 }}>Aucun donateur enregistré pour ce live.</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                        {liveDonors.map(d => (
                          <a key={d.donor_username} href={d.profile_url || `https://www.tiktok.com/@${d.donor_username}`} target="_blank" rel="noreferrer"
                            style={{ display: 'flex', alignItems: 'center', gap: 9, textDecoration: 'none', color: '#0f172a', borderRadius: 8, padding: '6px 4px' }}>
                            <LiveAvatar url={d.donor_avatar_url} initials={(d.donor_nickname || d.donor_username).slice(0, 2).toUpperCase()} size={30} isLive={!!d.is_live} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.donor_nickname || d.donor_username}</div>
                              <div style={{ fontSize: 10, color: '#94a3b8' }}>@{d.donor_username}</div>
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 800, color: '#b45309', background: '#fffbeb', borderRadius: 999, padding: '3px 8px' }}>🪙 {fmtNum(d.coins)} pièces</div>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {isOpen && selectedView === 'followers' && (
                  <div style={{ borderTop: '1px solid #e2e8f0', padding: '10px 12px', background: '#fff' }}>
                    {followersLoading && !followers[live.id] ? (
                      <div style={{ color: '#94a3b8', fontSize: 12 }}>Chargement des nouveaux abonnés...</div>
                    ) : liveFollowers.length === 0 ? (
                      <div style={{ color: '#94a3b8', fontSize: 12 }}>Aucun nouveau follower enregistré pour ce live.</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                        {liveFollowers.map(f => (
                          <a key={f.follower_username} href={`https://www.tiktok.com/@${f.follower_username}`} target="_blank" rel="noreferrer"
                            style={{ display: 'flex', alignItems: 'center', gap: 9, textDecoration: 'none', color: '#0f172a', borderRadius: 8, padding: '6px 4px' }}>
                            <LiveAvatar url={f.follower_avatar_url} initials={(f.follower_nickname || f.follower_username).slice(0, 2).toUpperCase()} size={30} isLive={false} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.follower_nickname || f.follower_username}</div>
                              <div style={{ fontSize: 10, color: '#94a3b8' }}>@{f.follower_username}</div>
                            </div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#0ea5e9', background: '#f0f9ff', borderRadius: 999, padding: '3px 8px' }}>+1 abonné</div>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MiniLiveMetric({ label, value, onClick, active }: { label: string; value: string; onClick?: () => void; active?: boolean }) {
  const clickable = !!onClick;
  return (
    <div onClick={onClick}
      style={{
        background: active ? '#eff6ff' : '#f8fafc',
        border: `1px solid ${active ? '#bfdbfe' : '#f1f5f9'}`,
        borderRadius: 8, padding: '5px 6px', minWidth: 0,
        cursor: clickable ? 'pointer' : 'default',
        transition: 'background 0.15s, border-color 0.15s',
      }}>
      <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.1 }}>{label}</div>
      <div style={{ fontSize: 12, color: '#0f172a', fontWeight: 800, marginTop: 2, lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
    </div>
  );
}

function MobileTabLayout({
  mobileTab, setMobileTab, statsContent, streamerId,
}: {
  mobileTab: 'actu' | 'live' | 'stats' | 'mylives' | 'follow';
  setMobileTab: (t: 'actu' | 'live' | 'stats' | 'mylives' | 'follow') => void;
  statsContent: React.ReactNode;
  streamerId?: number | null;
}) {
  const TABS: Array<{ id: 'actu' | 'live' | 'stats' | 'mylives' | 'follow'; icon: string; label: string }> = [
    { id: 'stats', icon: '📊', label: 'Mes stats' },
    ...(streamerId ? [{ id: 'mylives' as const, icon: '🎬', label: 'Mes lives' }] : []),
    { id: 'follow', icon: '✓', label: 'Abonne-toi' },
    { id: 'actu', icon: '📣', label: 'Actualités' },
    { id: 'live', icon: '🔴', label: 'En live' },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Contenu — chaque onglet gère son propre scroll */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {mobileTab === 'actu' && <ActualitesCard isMobile={true} />}
        {mobileTab === 'live' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 10px' }}>
            <LiveStreamersCard isMobile={true} />
          </div>
        )}
        {mobileTab === 'stats' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 12px' }}>
            {statsContent}
          </div>
        )}
        {mobileTab === 'mylives' && streamerId != null && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 12px' }}>
            <MyLivesCard streamerId={streamerId} isMobile={true} />
          </div>
        )}
        {mobileTab === 'follow' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 12px' }}>
            <FollowAgencyCard isMobile={true} />
          </div>
        )}
      </div>
      {/* Barre d'onglets fixe en bas */}
      <div style={{ flexShrink: 0, display: 'flex', background: '#fff', borderTop: '1px solid #e2e8f0', boxShadow: '0 -2px 8px rgba(0,0,0,0.06)' }}>
        {TABS.filter((tab, i, arr) => arr.findIndex(t => t.id === tab.id) === i).map(tab => (
          <button key={tab.id} onClick={() => setMobileTab(tab.id)}
            style={{ flex: '1 1 0', minWidth: 0, padding: '9px 2px 11px', border: 'none', background: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, cursor: 'pointer', color: mobileTab === tab.id ? '#2563eb' : '#94a3b8', transition: 'color 0.15s' }}>
            <span style={{ fontSize: 21, lineHeight: 1 }}>{tab.icon}</span>
            <span style={{ width: '100%', fontSize: 9, fontWeight: mobileTab === tab.id ? 700 : 400, letterSpacing: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tab.label}</span>
            {mobileTab === tab.id && <div style={{ width: 24, height: 2, borderRadius: 2, background: '#2563eb', marginTop: 1 }} />}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   StreamerTab
   ══════════════════════════════════════════════════════════ */
function StreamerTab({ isAdmin, streamerId, isMobile }: { isAdmin: boolean; streamerId: number | null; isMobile?: boolean }) {
  const [loading, setLoading] = useState(true);
  const [sN, setSN] = useState<Streamer | null>(null);
  const [sN1, setSN1] = useState<Streamer | null>(null);
  const [periodN, setPeriodN] = useState<string | null>(null);
  const [periodN1, setPeriodN1] = useState<string | null>(null);
  const [settings, setSettings] = useState<Record<string, string | null>>({});
  const [noStreamer, setNoStreamer] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [mobileTab, setMobileTab] = useState<'actu' | 'live' | 'stats' | 'mylives' | 'follow'>('stats');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [periodsRes, settingsRes] = await Promise.all([getPeriods(), getObjectiveSettings()]);
        const periods = periodsRes.data;
        if (!cancelled) setSettings(settingsRes.data);

        const pN = periods[0] ?? null;
        const pN1 = periods[1] ?? null;
        if (!cancelled) { setPeriodN(pN); setPeriodN1(pN1); }

        const [rN, rN1] = await Promise.all([
          pN ? getStreamers(pN) : getStreamers(),
          pN1 ? getStreamers(pN1) : Promise.resolve({ data: [] as Streamer[] }),
        ]);
        if (cancelled) return;

        const listN = rN.data;
        const listN1 = rN1.data;

              // For non-admin: backend always returns only own record (≤1 item) — use it directly.
        // For admin: find by explicit streamerId.
        const myN = isAdmin
          ? (streamerId != null ? listN.find(s => s.id === streamerId) ?? null : null)
          : (listN.find(s => s.id === streamerId) ?? listN[0] ?? null);
        const myN1 = isAdmin
          ? (streamerId != null ? listN1.find(s => s.id === streamerId) ?? null : null)
          : (listN1.find(s => s.id === streamerId) ?? listN1[0] ?? null);

        if (!myN) {
          setNoStreamer(true);
        } else {
          setSN(myN);
          setSN1(myN1);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [streamerId, retryKey]);

  if (loading) return <LoadingSpinner />;

  if (loadError && !sN) {
    return (
      <div style={{ padding: '40px 32px', textAlign: 'center', color: '#64748b', fontSize: 14 }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
        Erreur lors du chargement des données.
        <br />
        <button onClick={() => { setLoadError(false); setNoStreamer(false); setSN(null); setRetryKey(k => k + 1); }}
          style={{ marginTop: 12, padding: '8px 18px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#2563eb' }}>
          Réessayer
        </button>
      </div>
    );
  }

  if (noStreamer || (!sN && isAdmin)) {
    const noStreamerMsg = (
      <div style={{ padding: 32, textAlign: 'center', color: '#64748b', fontSize: 14, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>👤</div>
        Aucun compte streamer associé à votre profil.
      </div>
    );
    if (isMobile) {
      return <MobileTabLayout mobileTab={mobileTab} setMobileTab={setMobileTab} statsContent={noStreamerMsg} />;
    }
    return (
      <div style={{ padding: '22px 28px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', columnGap: 16, alignItems: 'start' }}>
          <div style={{ gridColumn: 1, gridRow: 1, paddingBottom: 16 }}>
            <ActualitesCard isMobile={false} />
          </div>
          <div style={{ gridColumn: 2, gridRow: '1 / 3' }}>
            <LiveStreamersCard isMobile={false} />
            <FollowAgencyCard isMobile={false} />
          </div>
          <div style={{ gridColumn: 1, gridRow: 2 }}>{noStreamerMsg}</div>
        </div>
      </div>
    );
  }

  if (!sN) return null;

  const objDiamonds = parseInt(settings['objective_diamonds'] ?? '750', 10) || 750;
  const objDays = parseInt(settings['objective_days'] ?? '8', 10) || 8;
  const objHours = parseInt(settings['objective_hours'] ?? '20', 10) || 20;

  const displayName = sN.display_name || sN.username;
  const isLive = sN.tiktok_live;

  const pctDiamonds = calcPct(sN.diamonds, sN1?.diamonds);
  const pctDays = calcPct(sN.live_days, sN1?.live_days);
  const pctHours = calcPct(sN.live_duration_minutes, sN1?.live_duration_minutes);
  const pctFollowers = calcPct(sN.new_followers, sN1?.new_followers);

  const statsSection = (
    <div style={{ minWidth: 0 }}>
      {/* Identity card */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '18px 22px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#ede9fe', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, color: '#7c3aed' }}>
          {sN.avatar_url
            ? <img src={sN.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : displayName.charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>@{sN.username}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, background: isLive ? '#dcfce7' : '#f1f5f9', borderRadius: 20, padding: '4px 12px' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: isLive ? '#16a34a' : '#94a3b8', display: 'inline-block' }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: isLive ? '#16a34a' : '#64748b' }}>{isLive ? 'En live' : 'Hors live'}</span>
        </div>
      </div>

      {/* Period */}
      {(periodN || periodN1) && (
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontWeight: 600, color: '#0f172a' }}>{periodN ? fmtPeriod(periodN) : '—'}</span>
          {periodN1 && <><span style={{ color: '#cbd5e1' }}>vs</span><span>{fmtPeriod(periodN1)}</span></>}
        </div>
      )}

      {/* Metrics */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <MetricCard icon="💎" label="Diamants" value={fmtNum(sN.diamonds)} pct={pctDiamonds} />
        <MetricCard icon="📅" label="Jours live" value={fmtNum(sN.live_days)} pct={pctDays} />
        <MetricCard icon="⏱" label="Heures live" value={fmtHours(sN.live_duration_minutes)} pct={pctHours} />
        <MetricCard icon="👥" label="Nouveaux abonnés" value={sN.new_followers != null ? fmtNum(sN.new_followers) : '—'} pct={pctFollowers} />
      </div>

      {/* Objectives + TikTok status */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 20px' }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a', marginBottom: 12 }}>Objectifs du mois</div>
        <ObjectiveGauge value={sN.diamonds ?? null} target={objDiamonds} label="💎 Diamants" />
        <ObjectiveGauge value={sN.live_days ?? null} target={objDays} label="📅 Jours live" />
        <ObjectiveGauge value={sN.live_duration_minutes != null ? Math.round(sN.live_duration_minutes / 60) : null} target={objHours} label="⏱ Heures live" />

        {/* TikTok status section */}
        {(() => {
          const echelonSt = parseEchelon(sN.statut_echelon);
          const days = sN.live_days ?? 0;
          const mins = sN.live_duration_minutes ?? 0;
          const activiteTier = getActiviteTier(days, mins);
          const nextTier = nextActiviteThreshold(activiteTier);
          // Conditions for niveau remuneration: 7j AND 15h (900 min)
          const niveauCondDays = days >= 7;
          const niveauCondHours = mins >= 900;
          const niveauCondMet = niveauCondDays && niveauCondHours;

          const echelonBadge = () => {
            if (!echelonSt) return <span style={{ fontSize: 12, color: '#94a3b8' }}>—</span>;
            const cfg: Record<EchelonStatus & string, { label: string; bg: string; color: string }> = {
              montee:      { label: '▲ Montée',       bg: '#dbeafe', color: '#1d4ed8' },
              maintien:    { label: '✓ Maintenu',      bg: '#dcfce7', color: '#16a34a' },
              non_maintenu: { label: '⚠ Non maintenu', bg: '#fef3c7', color: '#d97706' },
            };
            const c = cfg[echelonSt];
            return <span style={{ fontSize: 12, fontWeight: 600, background: c.bg, color: c.color, borderRadius: 10, padding: '2px 9px' }}>{c.label}</span>;
          };

          const tierLabel = activiteTier === 0
            ? <span style={{ fontSize: 12, color: '#94a3b8' }}>Inactif</span>
            : <span style={{ fontSize: 12, fontWeight: 600, background: '#f0fdf4', color: '#16a34a', borderRadius: 10, padding: '2px 9px' }}>Niveau {activiteTier}/5</span>;

          return (
            <div style={{ borderTop: '1px solid #f1f5f9', marginTop: 14, paddingTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 }}>Statut TikTok</div>

              {/* Montée de niveau */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>📊 Montée de niveau</span>
                {echelonBadge()}
              </div>
              {sN.statut_evolution && sN.statut_evolution !== '-' && (
                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4, paddingLeft: 2 }}>{sN.statut_evolution}</div>
              )}
              <div style={{ fontSize: 11, color: niveauCondMet ? '#16a34a' : '#94a3b8', marginBottom: 12, display: 'flex', gap: 10 }}>
                <span style={{ color: niveauCondDays ? '#16a34a' : '#f59e0b' }}>{niveauCondDays ? '✓' : '✗'} {days}/7 jours</span>
                <span style={{ color: niveauCondHours ? '#16a34a' : '#f59e0b' }}>{niveauCondHours ? '✓' : '✗'} {fmtHours(mins)}/15h</span>
              </div>

              {/* Niveau d'activité */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>⚡ Niveau d'activité</span>
                {tierLabel}
              </div>
              {activiteTier < 5 && nextTier && (
                <div style={{ fontSize: 11, color: '#94a3b8' }}>
                  Prochain niv. {nextTier.tier} : {nextTier.days}j et {nextTier.hours}h
                  {' · '}<span style={{ color: days >= nextTier.days ? '#16a34a' : '#f59e0b' }}>{days}/{nextTier.days}j</span>
                  {' · '}<span style={{ color: mins >= nextTier.hours * 60 ? '#16a34a' : '#f59e0b' }}>{fmtHours(mins)}/{nextTier.hours}h</span>
                </div>
              )}
            </div>
          );
        })()}
      </div>
      {/* Sur desktop : « Mes lives » reste dans le bloc stats.
          Sur mobile : la card a son propre onglet en bas (voir MobileTabLayout). */}
      {!isMobile && <MyLivesCard streamerId={sN.id} isMobile={isMobile} />}
    </div>
  );

  if (isMobile) {
    return <MobileTabLayout mobileTab={mobileTab} setMobileTab={setMobileTab} statsContent={statsSection} streamerId={sN.id} />;
  }

  return (
    <div style={{ padding: '22px 28px' }}>
      {/* Grille 2 colonnes explicites : col 1 = contenu, col 2 = En live */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', columnGap: 16, alignItems: 'start' }}>
        {/* Actualités — col 1 ligne 1 */}
        <div style={{ gridColumn: 1, gridRow: 1, paddingBottom: 16 }}>
          <ActualitesCard isMobile={false} />
        </div>
        {/* En live — col 2, spans lignes 1 et 2 */}
        <div style={{ gridColumn: 2, gridRow: '1 / 3' }}>
          <LiveStreamersCard isMobile={false} />
          <FollowAgencyCard isMobile={false} />
        </div>
        {/* Stats — col 1 ligne 2 */}
        <div style={{ gridColumn: 1, gridRow: 2 }}>
          {statsSection}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   LeaderTeamRow / LeaderTeamCard — streamer-style list items
   ══════════════════════════════════════════════════════════ */
interface Obj { days: number; hours: number; diamonds: number; }

function LeaderTeamCard({ m, obj, remuEnabled, onSelect }: { m: TeamMember; obj: Obj; remuEnabled?: boolean; onSelect?: (s: Streamer) => void }) {
  const initials = (m.display_name || m.username).slice(0, 2).toUpperCase();
  const isLive = !!m.tiktok_live;
  const allMet = (m.diamonds ?? 0) >= obj.diamonds && (m.live_days ?? 0) >= obj.days && (m.live_duration_minutes ?? 0) >= obj.hours * 60;
  const clickable = !!onSelect;
  return (
    <div
      onClick={clickable ? () => onSelect?.(m as unknown as Streamer) : undefined}
      style={{ background: '#fff', border: `1px solid ${isLive ? '#fecaca' : '#e2e8f0'}`, borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12, cursor: clickable ? 'pointer' : 'default', boxShadow: isLive ? '0 0 0 2px rgba(220,38,38,0.1)' : '0 1px 3px rgba(0,0,0,0.04)' }}>
      <LiveAvatar url={m.avatar_url} initials={initials} size={44} isLive={isLive} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.display_name || m.username}</div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>@{m.username}</div>
        {m.diamonds != null && (
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
            {fmtNum(m.diamonds)} 💎 · {m.live_days ?? 0}j · {fmtDuration(m.live_duration_minutes)}
          </div>
        )}
        {remuEnabled && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6, fontSize: 11, fontWeight: 700 }}>
            <span style={{ color: '#7c3aed' }}>Montée {fmtMoney(m.team_niveau)}</span>
            <span style={{ color: '#0891b2' }}>Activité {fmtMoney(m.team_activite)}</span>
            <span style={{ color: '#16a34a' }}>Total {fmtMoney(m.team_total)}</span>
          </div>
        )}
        <ObjBars s={m} obj={obj} />
      </div>
      <span style={{ fontSize: 14 }}>{allMet ? '✅' : ''}</span>
    </div>
  );
}

function LeaderTeamRow({ m, obj, remuEnabled, onSelect }: { m: TeamMember; obj: Obj; remuEnabled: boolean; onSelect?: (s: Streamer) => void }) {
  const [hover, setHover] = useState(false);
  const initials = (m.display_name || m.username).slice(0, 2).toUpperCase();
  const isLive = !!m.tiktok_live;
  const tiktokUrl = isLive ? `https://www.tiktok.com/@${m.username}/live` : `https://www.tiktok.com/@${m.username}`;
  const cols = remuEnabled
    ? '1fr 110px 70px 60px 80px 80px 80px 50px'
    : '1fr 110px 70px 60px 50px';
  const clickable = !!onSelect;

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={clickable ? () => onSelect?.(m as unknown as Streamer) : undefined}
      style={{ display: 'grid', gridTemplateColumns: cols, gap: 8, padding: '10px 14px', borderRadius: 10, background: hover ? '#fff' : 'transparent', border: `1px solid ${hover ? '#e2e8f0' : 'transparent'}`, cursor: clickable ? 'pointer' : 'default', alignItems: 'center', transition: 'all 0.12s', boxShadow: hover ? '0 1px 4px rgba(0,0,0,0.06)' : 'none' }}>
      {/* Avatar + nom */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <ObjThermo s={m} obj={obj} />
        <LiveAvatar url={m.avatar_url} initials={initials} size={38} isLive={isLive} />
        <div style={{ minWidth: 0 }}>
          <div style={{ color: '#0f172a', fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.display_name || m.username}</div>
          <div style={{ color: '#94a3b8', fontSize: 11 }}>@{m.username}</div>
        </div>
      </div>
      {/* Diamants */}
      <div style={{ color: '#0f172a', fontSize: 13, fontWeight: m.diamonds ? 600 : 400 }}>{fmtNum(m.diamonds)}</div>
      {/* Durée */}
      <div style={{ color: '#64748b', fontSize: 13 }}>{fmtDuration(m.live_duration_minutes)}</div>
      {/* Jours */}
      <div style={{ color: '#64748b', fontSize: 13 }}>{m.live_days != null ? m.live_days : '—'}</div>
      {/* Remu columns */}
      {remuEnabled && <>
        <div style={{ color: '#7c3aed', fontSize: 13, fontWeight: 500 }}>{fmtMoney(m.team_niveau)}</div>
        <div style={{ color: '#0891b2', fontSize: 13, fontWeight: 500 }}>{fmtMoney(m.team_activite)}</div>
        <div style={{ color: '#16a34a', fontSize: 13, fontWeight: 500 }}>{fmtMoney(m.team_total)}</div>
      </>}
      {/* TikTok */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <a href={tiktokUrl} target="_blank" rel="noreferrer"
          title={isLive ? 'Rejoindre le live' : 'Voir le profil'}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 8, background: isLive ? '#dc2626' : '#f1f5f9', textDecoration: 'none', flexShrink: 0 }}>
          <TikTokIcon color={isLive ? '#fff' : '#374151'} size={13} />
        </a>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   LeaderTab — use getTeams() so non-admin leaders see their full team
   ══════════════════════════════════════════════════════════ */
type LeaderFilter = 'live' | 'incomplete' | 'complete' | null;

export function LeaderTab({ groupe, isMobile, onSelectStreamer }: { groupe: string; isMobile?: boolean; onSelectStreamer?: (s: Streamer) => void }) {
  const [loading, setLoading] = useState(true);
  const [periods, setPeriods] = useState<string[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [myTeam, setMyTeam] = useState<Team | null>(null);
  const [liveMap, setLiveMap] = useState<Map<number, boolean>>(new Map());
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<LeaderFilter>(null);
  const [settings, setSettings] = useState<Record<string, string | null>>({});

  // Load periods + settings once
  useEffect(() => {
    getPeriods().then(r => {
      const seen = new Set<string>();
      const deduped = r.data.filter(p => {
        const k = p.match(/^(\d{4}-\d{2})/)?.[1] ?? p;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      setPeriods(deduped);
      if (deduped.length > 0) setSelectedPeriod(deduped[0]);
    }).catch(() => {});
    getObjectiveSettings().then(r => setSettings(r.data)).catch(() => {});
  }, [groupe]);

  // Load team data via getTeams() — no role filtering, accessible to all
  useEffect(() => {
    if (!selectedPeriod) return;
    let cancelled = false;
    setLoading(true);
    getTeams(selectedPeriod).then(r => {
      if (cancelled) return;
      setMyTeam(r.data.find(t => t.groupe === groupe) ?? null);
      setLoading(false);
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedPeriod, groupe]);

  // Live status refresh every 5s — getTeams() without period gives current live status
  const refreshLive = useCallback(async () => {
    try {
      const r = await getTeams();
      const team = r.data.find(t => t.groupe === groupe);
      if (team) {
        const map = new Map<number, boolean>();
        team.members.forEach(mb => map.set(mb.id, !!mb.tiktok_live));
        setLiveMap(map);
      }
    } catch {}
  }, [groupe]);

  useEffect(() => {
    refreshLive();
    const iv = setInterval(refreshLive, 5000);
    return () => clearInterval(iv);
  }, [refreshLive]);

  const obj: Obj = {
    days: parseInt(settings['objective_days'] ?? '8', 10) || 8,
    hours: parseInt(settings['objective_hours'] ?? '20', 10) || 20,
    diamonds: parseInt(settings['objective_diamonds'] ?? '750', 10) || 750,
  };

  const remuEnabled = myTeam?.remu_enabled ?? false;

  // Merge live status into members
  const members: TeamMember[] = (myTeam?.members ?? []).map(m => ({
    ...m,
    tiktok_live: liveMap.has(m.id) ? liveMap.get(m.id) : m.tiktok_live,
  }));

  // Filter + search
  const filtered = members.filter(m => {
    const name = (m.display_name || m.username).toLowerCase();
    if (search && !name.includes(search.toLowerCase()) && !m.username.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === 'live' && !m.tiktok_live) return false;
    const allMet = (m.diamonds ?? 0) >= obj.diamonds && (m.live_days ?? 0) >= obj.days && (m.live_duration_minutes ?? 0) >= obj.hours * 60;
    if (filter === 'complete' && !allMet) return false;
    if (filter === 'incomplete' && allMet) return false;
    return true;
  }).sort((a, b) => (b.diamonds ?? -1) - (a.diamonds ?? -1));

  // Team summary — from myTeam only
  const teamDiamonds        = myTeam?.total_diamonds ?? 0;
  const teamRemuNiveau      = myTeam?.team_remu_niveau ?? 0;
  const teamRemuActivite    = myTeam?.team_remu_activite ?? 0;
  const teamRemuTotal       = myTeam?.team_total_remu ?? 0;
  const showSummary         = !!myTeam;

  // Column headers depending on remu
  const cols = remuEnabled
    ? '1fr 110px 70px 60px 80px 80px 80px 50px'
    : '1fr 110px 70px 60px 50px';
  const headers = remuEnabled
    ? ['Streamer', '💎 Diamants', 'Durée', 'Jours', 'Montée niv.', 'Activité', 'Total récomp.', '']
    : ['Streamer', '💎 Diamants', 'Durée', 'Jours', ''];

  // Filter chip button style
  const chipStyle = (active: boolean, color: string) => ({
    padding: '4px 10px', borderRadius: 20, border: `1px solid ${active ? color : '#e2e8f0'}`,
    background: active ? color : '#fff', color: active ? '#fff' : '#64748b',
    fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 4,
  } as React.CSSProperties);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Title row + period selector */}
      <div style={{ padding: isMobile ? '10px 12px 8px' : '12px 28px 10px', background: '#fff', borderBottom: '1px solid #f1f5f9', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a', flex: 1 }}>Équipe — {groupe}</div>
        {periods.length > 0 && (
          <select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#374151', fontSize: 12, cursor: 'pointer', outline: 'none' }}>
            {periods.map(p => <option key={p} value={p}>{fmtPeriodShort(p)}</option>)}
          </select>
        )}
      </div>

      {/* Team summary banner */}
      {showSummary && (
        <div style={{ padding: isMobile ? '8px 12px' : '8px 28px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9', flexShrink: 0, display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>Total Team</span>
            <span style={{ fontSize: 11, color: '#94a3b8', background: '#e2e8f0', borderRadius: 10, padding: '1px 7px' }}>{myTeam?.member_count ?? members.length} streamer{(myTeam?.member_count ?? members.length) !== 1 ? 's' : ''}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>💎 Diamants</span>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{teamDiamonds.toLocaleString('fr-FR')}</span>
          </div>
          {remuEnabled && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>Montée niv.</span>
                <span style={{ fontWeight: 700, fontSize: 13, color: '#7c3aed' }}>{fmtMoney(teamRemuNiveau)}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>Activité</span>
                <span style={{ fontWeight: 700, fontSize: 13, color: '#0891b2' }}>{fmtMoney(teamRemuActivite)}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>Total récomp.</span>
                <span style={{ fontWeight: 700, fontSize: 13, color: '#16a34a' }}>{fmtMoney(teamRemuTotal)}</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Search + filters row */}
      <div style={{ padding: isMobile ? '8px 12px' : '8px 28px', background: '#fff', borderBottom: '1px solid #f1f5f9', flexShrink: 0, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: '0 0 180px' }}>
          <svg style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" strokeWidth="2">
            <circle cx="11" cy="11" r="8" /><path strokeLinecap="round" d="M21 21l-4.35-4.35" />
          </svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher…"
            style={{ width: '100%', padding: '6px 10px 6px 28px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#0f172a', fontSize: 12, boxSizing: 'border-box', outline: 'none' }} />
        </div>
        {/* Filter chips */}
        <button onClick={() => setFilter(f => f === 'live' ? null : 'live')} style={chipStyle(filter === 'live', '#dc2626')}>🔴 En live</button>
        <button onClick={() => setFilter(f => f === 'incomplete' ? null : 'incomplete')} style={chipStyle(filter === 'incomplete', '#f59e0b')}>⚠️ Incomplets</button>
        <button onClick={() => setFilter(f => f === 'complete' ? null : 'complete')} style={chipStyle(filter === 'complete', '#16a34a')}>✅ Complets</button>
        <div style={{ marginLeft: 'auto', fontSize: 12, color: '#94a3b8' }}>{filtered.length} streamer{filtered.length !== 1 ? 's' : ''}</div>
      </div>

      {/* Column headers (desktop) */}
      {!isMobile && (
        <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 8, padding: '6px 14px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
          {headers.map((label, i) => (
            <span key={i} style={{ color: '#94a3b8', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: 0.8,
              paddingLeft: i === 0 ? (8 + 10 + 38 + 10) : 0 }}>{label}</span>
          ))}
        </div>
      )}

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '10px 10px 80px' : '6px 14px 24px' }}>
        {loading ? (
          <LoadingSpinner />
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8', fontSize: 14 }}>
            <div style={{ fontSize: 32, marginBottom: 10, opacity: 0.4 }}>👥</div>
            {search || filter ? 'Aucun résultat' : 'Aucun membre dans cette équipe'}
          </div>
        ) : isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.map(m => <LeaderTeamCard key={m.id} m={m} obj={obj} remuEnabled={remuEnabled} onSelect={onSelectStreamer} />)}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filtered.map(m => <LeaderTeamRow key={m.id} m={m} obj={obj} remuEnabled={remuEnabled} onSelect={onSelectStreamer} />)}
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   AgenceTab — embed complet du module Streamers
   ══════════════════════════════════════════════════════════ */
export function AgenceTab({ isMobile, onSelectStreamer, refreshKey, onStreamerCreated }: {
  isMobile?: boolean;
  onSelectStreamer?: (s: Streamer) => void;
  refreshKey: number;
  onStreamerCreated?: () => void;
}) {
  return (
    <StreamerList
      onSelect={s => onSelectStreamer?.(s)}
      selected={null}
      refreshKey={refreshKey}
      onSelectedUpdated={() => {}}
      isAdmin={true}
      onStreamerCreated={onStreamerCreated}
      isMobile={isMobile}
    />
  );
}

/* ══════════════════════════════════════════════════════════
   Dashboard root — affiche uniquement le tab Streamer
   ══════════════════════════════════════════════════════════ */
export default function Dashboard({ isAdmin, isMobile }: Props) {
  const { user } = useAuth();
  const streamerId = user?.streamer_id ?? null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: '#f8fafc' }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
        <div style={{ padding: isMobile ? '14px 14px 12px' : '22px 32px 18px' }}>
          {!isMobile && (
            <>
              <h1 style={{ margin: 0, color: '#0f172a', fontSize: 22, fontWeight: 700 }}>Tableau de bord</h1>
              <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>Vos statistiques et performances</p>
            </>
          )}
          {isMobile && (
            <h1 style={{ margin: 0, color: '#0f172a', fontSize: 18, fontWeight: 700 }}>Tableau de bord</h1>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: isMobile ? 'hidden' : 'auto', display: 'flex', flexDirection: 'column' }}>
        <StreamerTab isAdmin={isAdmin} streamerId={streamerId} isMobile={isMobile} />
      </div>
    </div>
  );
}
